<template>
  <div class="products-search-page p-4">
    <BaseProductModal
      v-model="showProductModal"
      @closeProduct="openedProduct = ''"
      :code="openedProduct"
      @productUpdated="checkProductUpdate"
      @productDeleted="onProductDeleted"
    />
    <NewProductModal
      v-if="showNewProductModal"
      @close="showNewProductModal = false"
      @newProductCreated="openProduct"
    />

    <!-- Search row -->
    <div class="search-row">
      <select
        v-show="isSearchTab"
        class="price-level-select"
        v-model="selectedPriceLevel"
      >
        <option
          v-for="level in priceLevels"
          :key="level.value"
          :value="level.value"
        >
          {{ level.label }}
        </option>
      </select>
      <select
        v-show="isSearchTab"
        class="price-level-select"
        v-model="sortBy"
        :title="$t('message.products_page.sort_by')"
      >
        <option
          v-for="option in sortOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ $t(option.labelKey) }}
        </option>
      </select>
      <div class="search-wrap">
        <button
          class="clear-btn"
          v-if="searchInput !== ''"
          @click="clearSearch"
        >
          x
        </button>
        <input
          ref="searchInput"
          placeholder="Search..."
          v-model="searchInput"
          type="text"
          class="search-input"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="suggestOpen ? 'true' : 'false'"
          @input="onSearchTyped"
          @keydown.down.exact.prevent="moveSuggestion(1)"
          @keydown.up.exact.prevent="moveSuggestion(-1)"
          @keydown.enter.exact="pickSuggestion()"
          @keydown.ctrl.enter.exact.prevent="openFirstResult"
          @keydown.meta.enter.exact.prevent="openFirstResult"
          @keydown.esc.exact="onSearchEscape"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
        />
        <!-- Autocompletado (sólo Typesense): códigos que empiezan por lo
             escrito o títulos que lo contienen, mientras se escribe.
             Dentro de un contenedor que siempre existe: si el panel fuera
             hermano directo del input, al escribir la primera letra cambiarían
             a la vez la "x" (aparece) y el panel (se cierra), Vue movería el
             input en el DOM y éste perdería el foco. -->
        <div class="suggest-anchor">
          <div
            v-if="suggestOpen"
            class="suggest-panel"
            role="listbox"
            @mousedown.prevent
          >
            <!-- Con la barra vacía: las últimas búsquedas de este usuario. -->
            <template v-if="panelMode === 'recent'">
              <div class="suggest-section">
                <span>Recent searches</span>
                <button class="suggest-clear" @click="clearRecentSearches">
                  Clear
                </button>
              </div>
              <div
                v-for="(recent, index) in recents"
                :key="recent"
                class="suggest-row recent-row"
                :class="{ 'is-active': index === suggestIndex }"
                role="option"
                :aria-selected="index === suggestIndex ? 'true' : 'false'"
                @click="pickSuggestion(index)"
              >
                <i class="mdi mdi-history recent-icon"></i>
                <span class="recent-text">{{ recent }}</span>
                <button
                  class="suggest-open"
                  title="Remove from history"
                  @click.stop="removeRecentSearch(recent)"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </template>
            <!-- Vehículo reconocido en lo escrito: se elige aquí mismo, sin
               cerrar el panel. Si no es una versión concreta, primero el
               año y luego el motor. -->
            <div
              v-if="panelMode === 'suggest' && vinNotice"
              class="suggest-section is-vehicle"
            >
              <span><i class="mdi mdi-barcode-scan"></i> {{ vinNotice }}</span>
            </div>
            <template v-if="panelMode === 'suggest' && panelVehicleRows.length">
              <div class="suggest-section is-vehicle">
                <span
                  ><i class="mdi mdi-truck-outline"></i>
                  {{ vehiclePanelTitle }}</span
                >
                <button
                  v-if="vehiclePickYear"
                  class="suggest-clear is-back"
                  @click="vehiclePickYear = null"
                >
                  ← Years
                </button>
              </div>
              <div
                v-for="(row, index) in panelVehicleRows"
                :key="row.key"
                class="suggest-row vehicle-row"
                :class="{ 'is-active': index === suggestIndex }"
                role="option"
                :aria-selected="index === suggestIndex ? 'true' : 'false'"
                @click="pickSuggestion(index)"
              >
                <div class="suggest-thumb">
                  <img v-if="row.image" :src="row.image" alt="" />
                  <i v-else class="mdi mdi-truck-outline"></i>
                </div>
                <div class="suggest-body">
                  <div class="suggest-code">{{ row.label }}</div>
                  <div v-if="row.hint" class="suggest-title">
                    {{ row.hint }}
                  </div>
                </div>
                <span class="vehicle-row-action">{{
                  row.kind === "year" ? "Engines ›" : "Select"
                }}</span>
              </div>
            </template>
            <div
              v-for="(suggestion, index) in panelMode === 'suggest'
                ? suggestions
                : []"
              :key="suggestion.id"
              class="suggest-row"
              :class="{
                'is-active': index + panelVehicleRows.length === suggestIndex,
                'is-nofit': suggestion.fits === false
              }"
              role="option"
              :aria-selected="
                index + panelVehicleRows.length === suggestIndex
                  ? 'true'
                  : 'false'
              "
              @click="pickSuggestion(index + panelVehicleRows.length)"
            >
              <div class="suggest-thumb">
                <img v-if="suggestion.image" :src="suggestion.image" alt="" />
                <i v-else class="mdi mdi-image-off-outline"></i>
              </div>
              <div class="suggest-body">
                <div class="suggest-head">
                  <span class="suggest-code">{{ suggestion.code }}</span>
                  <span v-if="suggestion.brand" class="suggest-brand">{{
                    suggestion.brand
                  }}</span>
                  <span
                    v-if="suggestion.available"
                    class="suggest-available"
                    :class="availabilityClass(suggestion.available)"
                    >{{ suggestion.available }}</span
                  >
                  <!-- Con vehículo elegido: si esta pieza le sirve. -->
                  <span
                    v-if="suggestion.fits !== undefined"
                    class="suggest-fit"
                    :class="suggestion.fits ? 'is-fit' : 'is-no'"
                    :title="vehicleFilter ? vehicleFilter.label : ''"
                  >
                    <i
                      :class="
                        suggestion.fits
                          ? 'mdi mdi-check-decagram'
                          : 'mdi mdi-close-circle-outline'
                      "
                    ></i>
                    {{ suggestion.fits ? "Fits" : "Not for this vehicle" }}
                  </span>
                </div>
                <div v-if="suggestion.label" class="suggest-match">
                  <span class="suggest-label">{{ suggestion.label }}</span>
                  <span class="suggest-value" v-html="suggestion.html" />
                </div>
                <div class="suggest-title" v-html="suggestion.titleHtml" />
              </div>
              <button
                class="suggest-open"
                title="Open product"
                @click.stop="openSuggestion(suggestion)"
              >
                <i class="mdi mdi-open-in-new"></i>
              </button>
            </div>
            <div class="suggest-footer">
              <span v-if="panelMode === 'suggest'"
                >Typesense · {{ suggestFound }} matches ·
                {{ suggestTimeMs }} ms</span
              >
              <span v-else>This browser only</span>
              <span class="suggest-keys"
                >/ focus · ↑↓ · Enter · ⌘/Ctrl+Enter open first · Esc</span
              >
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="isSearchTab && !showBarcodeReader"
        @click="showBarcodeReader = true"
        class="barcode-btn"
      >
        <img src="@/assets/icons/barcode-reader.svg" class="w-8 h-8" />
      </button>
    </div>

    <!-- Barcode reader -->
    <div class="barcode-box relative" v-if="showBarcodeReader && isSearchTab">
      <button
        class="btn-dark-button absolute right-4 top-4 z-50 w-10 h-10 flex justify-center items-center"
        @click="closeReader()"
      >
        X
      </button>
      <StreamBarcodeReader
        ref="scanner"
        @decode="barcodeOnDecode"
        @loaded="barcodeOnLoaded"
      />
    </div>

    <!-- Tabs row -->
    <div class="tabs-row">
      <div class="tab-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'search' }"
          @click="activeTab = 'search'"
        >
          Parts Search
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'products' }"
          @click="activeTab = 'products'"
        >
          Products
        </button>
      </div>
      <span v-if="isSearchTab && searchStats" class="search-stats">
        Typesense: {{ searchStats.found }} results · {{ searchStats.ms }} ms
      </span>
      <!-- Por qué cada resultado está en su posición. -->
      <label v-if="isSearchTab" class="explain-toggle">
        <input v-model="explainRanking" type="checkbox" />
        Explain ranking
      </label>
      <span v-if="searchError" class="search-error">{{ searchError }}</span>
      <button
        v-if="activeTab === 'products'"
        class="btn-info-button"
        @click="showNewProductModal = true"
      >
        {{ $t("message.products_page.add_product") }}
      </button>
    </div>

    <!-- ── Search Tab ── -->
    <div v-show="isSearchTab" class="tab-content">
      <div class="search-layout">
        <!-- Filters sidebar -->
        <div class="filters" :class="{ 'mobile-show': showFilters }">
          <Filters
            v-if="facets && Object.keys(facets).length"
            :facets="facets"
            v-model="facetsSelection"
            @selectingVehicle="
              e => {
                vehicleSelectionInProcess = e;
              }
            "
            :vehicleSelectionInProcess="vehicleSelectionInProcess"
            @close="showFilters = false"
          />
          <button
            id="close-filters-button"
            class="absolute right-0 top-24 bg-black rounded-l-md w-10 h-8 text-white"
            v-if="showFilters && !vehicleSelectionInProcess"
            @click="showFilters = false"
          >
            X
          </button>
        </div>

        <!-- Items area -->
        <div class="search-items-area" :class="{ 'mobile-show': showFilters }">
          <!-- Typesense: vehículo elegido (reconocido en lo escrito). -->
          <div v-if="selectedVehicle" class="vehicle-card is-selected">
            <div class="vehicle-image">
              <img
                v-if="selectedVehicle.image"
                :src="selectedVehicle.image"
                alt=""
              />
              <i v-else class="mdi mdi-truck-outline"></i>
            </div>
            <div class="vehicle-body">
              <span class="vehicle-kicker">Parts for</span>
              <span class="vehicle-name">{{ selectedVehicle.valueLabel }}</span>
              <span class="vehicle-detail">{{ selectedVehicle.detail }}</span>
            </div>
            <button
              class="vehicle-remove"
              title="Remove vehicle"
              @click="removeVehicle"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>

          <!-- Typesense: "quisiste decir" (palabras corregidas por typo) y
               palabras que no se encontraron y se buscaron sin ellas. -->
          <div v-if="queryInfo.corrections.length" class="query-note">
            <i class="mdi mdi-spellcheck"></i>
            <span>
              Showing results for
              <strong>{{ correctedQuery }}</strong>
              <span class="query-note-muted"
                >· you typed “{{ searchInput }}”</span
              >
            </span>
            <button class="query-note-action" @click="useCorrectedQuery">
              Use “{{ correctedQuery }}”
            </button>
          </div>
          <div v-if="queryInfo.dropped.length" class="query-note is-warning">
            <i class="mdi mdi-text-search"></i>
            <span>
              Nothing matched every word. Ignored:
              <s v-for="word in queryInfo.dropped" :key="word" class="mr-1">{{
                word
              }}</s>
            </span>
            <button
              v-if="removableDroppedWords.length"
              class="query-note-action"
              @click="removeDroppedWords"
            >
              Remove from search
            </button>
          </div>

          <!-- Typesense: filtros rápidos de disponibilidad, con su cuenta. -->
          <div v-if="quickFilters.length" class="quick-filters">
            <button
              v-for="quick in quickFilters"
              :key="quick.key"
              class="quick-chip"
              :class="[quick.tone, { 'is-on': quick.on }]"
              @click="toggleQuickFilter(quick)"
            >
              <i :class="quick.on ? 'mdi mdi-check' : quick.icon"></i>
              {{ quick.label }}
              <span class="quick-count">{{ quick.count }}</span>
            </button>
          </div>

          <!-- Typesense: vehículo reconocido en lo escrito, con la imagen de
               su familia (ver utils/vehicleIntent.js). -->
          <div v-if="vinNotice" class="query-note">
            <i class="mdi mdi-barcode-scan"></i>
            <span>{{ vinNotice }}</span>
          </div>
          <div v-if="detectedVehicle" class="vehicle-card is-detected">
            <div class="vehicle-image">
              <img
                v-if="detectedVehicle.image"
                :src="detectedVehicle.image"
                alt=""
              />
              <i v-else class="mdi mdi-truck-outline"></i>
            </div>
            <div class="vehicle-body">
              <span class="vehicle-kicker">
                <i
                  :class="
                    detectedVehicle.source === 'vin'
                      ? 'mdi mdi-barcode-scan'
                      : 'mdi mdi-auto-fix'
                  "
                ></i>
                {{ detectedVehicle.kicker }}
              </span>
              <span class="vehicle-name">{{
                specificDetected
                  ? specificDetected.label
                  : detectedVehicle.label
              }}</span>
              <span class="vehicle-detail">{{
                specificDetected
                  ? specificDetected.detail
                  : detectedVehicle.detail
              }}</span>
              <!-- Varias versiones: se concreta antes de filtrar, primero el
                   año (si no se escribió) y luego el motor. -->
              <div v-if="!specificDetected" class="vehicle-options">
                <span class="vehicle-options-title">{{
                  vehicleChoice.title
                }}</span>
                <button
                  v-if="vehiclePickYear"
                  class="vehicle-option is-back"
                  @click="vehiclePickYear = null"
                >
                  <i class="mdi mdi-arrow-left"></i> Years
                </button>
                <button
                  v-for="option in vehicleChoice.options"
                  :key="option.key"
                  class="vehicle-option"
                  :title="option.hint"
                  @click="pickVehicleOption(option)"
                >
                  {{ option.label }}
                  <span v-if="option.hint" class="vehicle-option-hint">{{
                    option.hint
                  }}</span>
                </button>
              </div>
            </div>
            <button
              v-if="specificDetected"
              class="vehicle-select"
              @click="applyIntent([], specificDetected)"
            >
              <i class="mdi mdi-truck-check-outline"></i>
              Search parts for this vehicle
            </button>
          </div>

          <!-- Typesense: filtros reconocidos en lo escrito (motor, vehículo,
               categoría, marca). Un clic los aplica y quita esas palabras
               del texto (ver utils/searchIntent.js). -->
          <div v-if="detectedFilters.length" class="intent-bar">
            <span class="intent-title">
              <i class="mdi mdi-auto-fix"></i>
              Detected in your search
            </span>
            <button
              v-for="match in detectedFilters"
              :key="`${match.type}|${match.value}`"
              class="intent-chip"
              :title="`Filter by ${match.kind.toLowerCase()} ${match.value}`"
              @click="applyIntent([match])"
            >
              <i class="mdi mdi-plus"></i>
              <span class="intent-kind">{{ match.kind }}</span>
              <span class="intent-value">{{ match.display }}</span>
              <span v-if="intentCount(match)" class="intent-count">{{
                intentCount(match)
              }}</span>
            </button>
            <button
              v-if="detectedFilters.length + (specificDetected ? 1 : 0) > 1"
              class="intent-apply-all"
              @click="applyIntent(detectedFilters, specificDetected)"
            >
              Apply all
            </button>
          </div>

          <!-- Active facet chips -->
          <div
            v-if="chipFacets.length > 0"
            class="flex flex-row w-full flex-wrap border-b border-b-gray-400 pb-2 mb-2 pt-1"
          >
            <div class="flex-grow flex flex-row flex-wrap mb-1">
              <DeleteButton
                v-for="(facet, index) in chipFacets"
                :key="index"
                class="mb-1 mr-1"
                @click="deleteFacet(facet)"
              >
                {{ facet.valueLabel ? facet.valueLabel : facet.value }}
              </DeleteButton>
            </div>
            <button
              class="text-sm text-red-500 hover:underline mb-1 ml-2 whitespace-nowrap"
              @click="clearAllFacets"
            >
              Clear all
            </button>
          </div>

          <!-- Loading -->
          <div class="loading-state" v-if="searchLoading">
            <BaseMiniSpinner />
            <span>Loading...</span>
          </div>

          <!-- Items grid -->
          <div class="items" :class="{ loading: searchLoading }">
            <div
              v-for="item in items"
              :key="item.nkrCode[0]"
              class="item"
              @click="openResult(item.nkrCode[0])"
            >
              <span v-if="explainRanking && item._ranking" class="rank-badge"
                >#{{ item._ranking.position }}</span
              >
              <div class="item-image">
                <img
                  v-if="item.image_thumbnail && item.image_thumbnail !== ''"
                  :src="item.image_thumbnail"
                  :alt="`product-image-${item.nkrCode[0]}`"
                />
                <img
                  v-else-if="item.image_url"
                  :src="item.image_url"
                  :alt="`product-image-${item.nkrCode[0]}`"
                />
                <img
                  v-else
                  src="@/assets/img/no-image-found.png"
                  :alt="`no-image-${item.nkrCode[0]}`"
                />
              </div>
              <div class="item-info">
                <div class="item-header">
                  <h2 class="item-code">{{ item.nkrCode[0] }}</h2>
                  <span class="item-brand">{{ item.brand }}</span>
                </div>
                <button
                  v-if="item.unificado"
                  class="item-unificado"
                  @click.stop="setSearch(item.unificado)"
                >
                  {{ item.unificado }}
                </button>
                <p
                  class="item-title"
                  v-if="
                    item._highlightResult &&
                      item._highlightResult.title &&
                      item._highlightResult.title.value
                  "
                  v-html="item._highlightResult.title.value"
                />
                <p class="item-title" v-else>{{ item.title }}</p>
                <!-- Typesense: dónde coincidió la búsqueda si no fue en el título -->
                <div
                  v-if="item._matches && item._matches.length"
                  class="item-matches"
                >
                  <div
                    v-for="match in item._matches"
                    :key="match.field"
                    class="item-match"
                  >
                    <span class="match-label">{{ match.label }}</span>
                    <span
                      v-for="(value, index) in match.values"
                      :key="index"
                      class="match-value"
                      v-html="value"
                    />
                    <span v-if="match.more" class="match-more"
                      >+{{ match.more }}</span
                    >
                  </div>
                </div>
                <div class="item-price" v-if="getItemPrice(item)">
                  ${{ getItemPrice(item) }}
                </div>
                <!-- Typesense: doble verificación con el vehículo elegido, contra
                     el fitment del propio producto (no sólo el filtro). -->
                <div
                  v-if="vehicleFilter && item._fits !== undefined"
                  class="fit-badge"
                  :class="item._fits ? 'is-fit' : 'is-unknown'"
                  :title="
                    item._fits
                      ? 'This part lists the vehicle in its applications'
                      : 'The vehicle is not in this part applications'
                  "
                >
                  <i
                    :class="
                      item._fits
                        ? 'mdi mdi-check-decagram'
                        : 'mdi mdi-alert-outline'
                    "
                  ></i>
                  <span v-if="item._fits">Fits {{ vehicleFilter.label }}</span>
                  <span v-else>Not listed for {{ vehicleFilter.label }}</span>
                </div>
                <!-- Modo "por qué salió" (Typesense) -->
                <div
                  v-if="explainRanking && item._ranking"
                  class="rank-explain"
                  @click.stop
                >
                  <template v-if="item._ranking.textSearch">
                    <div>
                      <span class="rank-key">Matched in</span>
                      {{ item._ranking.fields.join(", ") || "—" }}
                    </div>
                    <div>
                      <span class="rank-key">Match</span>
                      {{
                        item._ranking.exact
                          ? "exact words"
                          : "with typo or partial word"
                      }}
                      · {{ item._ranking.tokensMatched }} word(s)
                      <template v-if="item._ranking.tokensDropped">
                        · {{ item._ranking.tokensDropped }} ignored
                      </template>
                    </div>
                  </template>
                  <div>
                    <span class="rank-key">Order</span>
                    {{ rankingReason(item._ranking) }}
                  </div>
                </div>
                <div class="item-location" v-if="getItemLocation(item)">
                  <span class="loc-label">{{ activeDeptCode }}</span>
                  <span class="loc-value">{{ getItemLocation(item) }}</span>
                </div>
              </div>
              <div class="item-stock" v-if="item.price_and_stock">
                <div class="stock-total">
                  <span class="stock-label">Stock:</span>
                  <span class="stock-value">
                    {{ item.price_and_stock.totalStock }}
                    {{ item.product_type || "PCS" }}
                  </span>
                </div>
                <div
                  class="stock-breakdown"
                  v-if="item.price_and_stock_by_store"
                >
                  <template
                    v-for="(storeData,
                    storeCode) in item.price_and_stock_by_store"
                  >
                    <template
                      v-if="
                        storeData.stockByWarehouse &&
                          Object.keys(storeData.stockByWarehouse).length > 0
                      "
                    >
                      <div :key="storeCode" class="store-stock">
                        <span class="store-name">{{ storeCode }}</span>
                        <span
                          v-for="(warehouseData,
                          warehouseCode) in storeData.stockByWarehouse"
                          :key="`${storeCode}-${warehouseCode}`"
                          class="warehouse-stock"
                        >
                          {{ warehouseCode }}:
                          <strong>{{ warehouseData.stock || 0 }}</strong>
                        </span>
                      </div>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Typesense: scroll infinito. El observer carga la página
               siguiente al acercarse; el botón por si no hay scroll. -->
          <div v-if="tsHasMore" ref="loadMoreSentinel" class="load-more">
            <BaseMiniSpinner v-if="tsLoadingMore" />
            <button v-else class="load-more-btn" @click="loadMoreTypesense">
              Load more ({{ items.length }} of {{ searchStats.found }})
            </button>
          </div>

          <!-- Vehicle selection overlay -->
          <div
            v-show="vehicleSelectionInProcess"
            class="w-full h-full absolute left-0 top-0 bg-black bg-opacity-60"
          ></div>
        </div>
      </div>

      <!-- Show Filters button (mobile) -->
      <button
        class="show-filters-btn"
        v-if="!showFilters"
        @click="showFilters = true"
      >
        <span>Show Filters</span>
        <img class="w-6" src="@/assets/icons/filter.svg" alt="filters" />
      </button>
    </div>

    <!-- ── Products Tab ── -->
    <div v-show="activeTab === 'products'" class="tab-content">
      <!-- Status filter bar -->
      <div class="status-filter-bar">
        <button
          v-for="opt in productStatusOptions"
          :key="String(opt.value)"
          class="status-btn"
          :class="{ active: productFilters.storeEnabled === opt.value }"
          @click="changeStatusFilter(opt.value)"
        >
          {{ opt.displayText }}
        </button>
        <select
          class="dep-select"
          v-model="productFilters.dependencia"
          @change="onDependenciaChange"
        >
          <option value="">All dep.</option>
          <option
            v-for="dep in dependenciaOptions"
            :key="dep.value"
            :value="dep.value"
          >
            {{ dep.value }} — {{ dep.label }}
          </option>
        </select>
        <BaseMiniSpinner v-if="productsLoading" class="ml-2" />
      </div>

      <div class="products-table-wrap">
        <table class="products-table">
          <thead>
            <tr>
              <th
                v-for="headerItem in tableHeaders"
                :key="headerItem.value"
                @click="changeArrowDir(headerItem.value)"
                :class="headerItem.visible"
              >
                {{ headerItem.displayText }}
                <span
                  v-if="productFilters.orderCol == headerItem.value"
                  class="sort-icon"
                >
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in products"
              :key="product.nkrCode"
              @click="openProduct(product.nkrCode)"
              :class="{ selected: selectedProduct == product.id }"
            >
              <td :class="tableHeaders[0].visible">
                <div class="sku-cell">
                  <span
                    class="sku-code"
                    :class="{
                      match:
                        productFilters.search.replace('-', '') ===
                        product.nkrCode.replace('-', '')
                    }"
                  >
                    {{ product.nkrCode }}</span
                  >
                  <span class="sku-brand">{{ product.brand }}</span>
                </div>
              </td>
              <td :class="tableHeaders[1].visible" class="store-brands-cell">
                {{ product.storeBrands || "" }}
              </td>
              <td :class="tableHeaders[2].visible">
                <button
                  class="code-btn"
                  :class="{
                    match:
                      productFilters.search.replace('-', '') ==
                      product.unificado.replace('-', '')
                  }"
                  @click.stop="setSearch(product.unificado)"
                >
                  {{ product.unificado }}
                </button>
              </td>
              <td :class="tableHeaders[3].visible">
                <button
                  class="code-btn"
                  :class="{
                    match:
                      productFilters.search.replace('-', '') ==
                      product.oe.replace('-', '')
                  }"
                  @click.stop="setSearch(product.oe)"
                >
                  {{ product.oe }}
                </button>
              </td>
              <td
                v-if="userCanSeeCost"
                :class="tableHeaders[4].visible"
                class="text-center"
              >
                ${{ product.cost }}
              </td>
              <td :class="tableHeaders[5].visible">
                <div class="title-cell">
                  <span class="title-custom">{{ product.customTitle }}</span>
                  <span class="title-original">
                    {{ product.originalTitle }}</span
                  >
                </div>
              </td>
              <td :class="tableHeaders[6].visible" class="text-center">
                <div class="stock-cell">
                  <div class="stock-current">
                    <span class="stock-label">{{ activeDeptCode || "—" }}</span>
                    <span class="stock-value">{{
                      currentDeptStock(product)
                    }}</span>
                  </div>
                  <div class="stock-all">
                    <span class="stock-label">All</span>
                    <span class="stock-value">
                      {{ allDeptStock(product).total }}
                      <span class="stock-dpts">
                        ({{ allDeptStock(product).count }} dpts)
                      </span>
                    </span>
                  </div>
                </div>
              </td>
              <td :class="tableHeaders[7].visible" class="text-center">
                <BaseSwitchCustom
                  v-model="product.enabled"
                  @change="
                    updateProductStatus(product.nkrCode, product.enabled)
                  "
                  @click.native.stop
                />
              </td>
              <td :class="tableHeaders[8].visible">
                <div class="prices-cell" @click.stop>
                  <button class="prices-btn">Prices</button>
                  <div class="prices-popover">
                    <table class="prices-popover-table">
                      <thead>
                        <tr>
                          <th>Dept</th>
                          <th>Line</th>
                          <th>Stock</th>
                          <th>O</th>
                          <th>R</th>
                          <th>W</th>
                          <th>P4</th>
                          <th>P5</th>
                          <th>P6</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, idx) in product.pricesAndStock || []"
                          :key="idx"
                          :class="{
                            'is-current':
                              row.departamentoCodigo === activeDeptCode
                          }"
                        >
                          <td>{{ row.departamentoCodigo }}</td>
                          <td>{{ row.line }}</td>
                          <td class="text-center">{{ row.stock }}</td>
                          <td>{{ row.online | currencyFormatted(true) }}</td>
                          <td>
                            {{ row.repairShop | currencyFormatted(true) }}
                          </td>
                          <td>{{ row.wholesale | currencyFormatted(true) }}</td>
                          <td>{{ row.precio4 | currencyFormatted(true) }}</td>
                          <td>{{ row.precio5 | currencyFormatted(true) }}</td>
                          <td>{{ row.precio6 | currencyFormatted(true) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
              <td class="sm:hidden text-center">
                <button
                  class="px-3 py-1 bg-gray-500 rounded text-white text-sm"
                  @click.stop="openProduct(product.nkrCode)"
                >
                  {{ $t("message.products_page.open") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination footer -->
      <div class="footer pt-8 mb-10">
        <div class="totals w-full flex justify-center mb-4">
          {{ $t("message.products_page.showing") }}
          {{ productFilters.limitPerPage * (productFilters.page - 1) }}
          {{ $t("message.products_page.to") }} {{ toQuantity() }}
          {{ $t("message.products_page.from") }} {{ totalItems }}
          {{ $t("message.products_page.results") }}
        </div>
        <div class="pages w-full flex flex-row justify-center">
          <select
            v-model="productsByPage"
            @change="onPageSizeChange"
            class="border-light-100 mr-4"
          >
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="500">500</option>
          </select>
          {{ $t("message.products_page.items_per_page") }}
        </div>
        <div class="pages flex flex-row w-full justify-center mt-4">
          <a
            :class="{ disabled: productFilters.page == 1 }"
            class="page-link mr-4 bg-gray-400 rounded-md px-2 text-white"
            @click="previous()"
            >{{ $t("message.products_page.previous") }}</a
          >
          <div>
            {{ $t("message.products_page.page") }} {{ productFilters.page }}
          </div>
          <a
            :class="{ disabled: productFilters.page >= totalPages }"
            class="page-link ml-4 bg-gray-400 rounded-md px-2 text-white"
            @click="next()"
            >{{ $t("message.products_page.next") }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { StreamBarcodeReader } from "vue-barcode-reader";
import {
  searchProducts as searchTypesense,
  suggestProducts,
  loadFacetValues,
  loadVehicles
} from "@/utils/productSearch";
import {
  readRecents,
  addRecent,
  removeRecent,
  clearRecents
} from "@/utils/searchRecents";
import {
  INTENT_FIELDS,
  buildVocabulary,
  detectIntent,
  removeWordIndices
} from "@/utils/searchIntent";
import {
  isVin,
  isVinTail,
  vinYears,
  decodeVin,
  decodedLabel
} from "@/utils/vinDecode";
import { findVinsByTail } from "@/utils/typesenseOrders";
import {
  buildVehicleIndex,
  detectVehicle,
  vehicleFromVin,
  specificVehicle,
  engineLabel as vehicleEngineLabel
} from "@/utils/vehicleIntent";
import {
  SORT_OPTIONS,
  DEFAULT_SORT,
  isSortOption
} from "@/constants/sortOptions";
import { mapGetters } from "vuex";
import Filters from "@/components/search/Filters.vue";
import DeleteButton from "@/components/search/DeleteButton.vue";
import NewProductModal from "@/components/product/NewProductModal.vue";
import {
  getProducts,
  changeProductStatus
} from "@/api/store/ProductsService.js";
import { getProductStats } from "@/api/store/Statistics.js";

// Modo "por qué salió": se recuerda en este navegador.
const EXPLAIN_STORAGE_KEY = "partSearch.explain";
const readExplain = () => {
  try {
    return window.localStorage.getItem(EXPLAIN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};
const TYPESENSE_PAGE_SIZE = 50;
// Años que el autocompletado ofrece para concretar el vehículo (los más
// recientes); el resto, escribiendo el año o en la tarjeta de resultados.
const PANEL_MAX_YEARS = 6;
// Igual que en storeSearchQueryInfo de la API, para casar lo escrito con
// las palabras de queryInfo.
const cleanWord = word => word.toLowerCase().replace(/[^a-z0-9]/g, "");
const EMPTY_QUERY_INFO = { corrections: [], dropped: [] };

// Autocompletado: pausa tras la última tecla y sugerencias por lista.
const SUGGEST_DEBOUNCE_MS = 80;
const SUGGEST_LIMIT = 8;

// Pestañas que abre ?view=. "algolia" y "typesense" son las de antes de quitar
// Algolia: los enlaces que ya se compartieron abren la búsqueda.
const LEGACY_SEARCH_VIEWS = ["algolia", "typesense"];

export default {
  name: "ProductsSearchPage",
  components: {
    StreamBarcodeReader,
    Filters,
    DeleteButton,
    NewProductModal
  },
  data() {
    return {
      // shared
      searchInput: "",
      activeTab: "search",
      openedProduct: "",
      showProductModal: false,
      isInitializing: true,
      // search
      items: [],
      searchLoading: false,
      searchCallId: 0,
      facets: {},
      facetsSelection: [],
      vehicleSelectionInProcess: false,
      showFilters: false,
      selectedPriceLevel: "online",
      priceLevelFromUrl: false,
      sortBy: DEFAULT_SORT,
      showBarcodeReader: false,
      searchStats: null,
      searchError: null,
      // Autocompletado de Typesense
      suggestions: [],
      suggestIndex: -1,
      suggestFound: 0,
      suggestTimeMs: 0,
      suggestFocused: false,
      suggestCallId: 0,
      // Lo escrito que pidió sugerencias; null al cerrar la lista, para que
      // una petición pendiente (debounce) no la vuelva a abrir.
      suggestFor: null,
      // Vocabulario de facets para reconocer filtros en lo escrito, y la
      // colección de la que salió.
      intentVocabulary: null,
      intentCollection: null,
      // Vehículos y familias para reconocer el vehículo en lo escrito, y la
      // tienda de la que salieron.
      vehicleIndex: null,
      vehicleIndexStore: null,
      // Año elegido en la tarjeta cuando lo escrito no lo dice.
      vehiclePickYear: null,
      // VIN decodificados (utils/vinDecode.js): { [vin]: { status, decoded } }
      vinStates: {},
      // Últimos 8 de un VIN buscados en las órdenes (findVinsByTail):
      // { [tail]: { status, vins } }
      vinTailStates: {},
      // Últimas búsquedas y panel cerrado con Esc hasta volver a escribir.
      recents: [],
      panelDismissed: false,
      // "Quisiste decir" de la última búsqueda de Typesense.
      queryInfo: EMPTY_QUERY_INFO,
      explainRanking: readExplain(),
      // Scroll infinito de Typesense.
      tsPage: 1,
      tsHasMore: false,
      tsLoadingMore: false,
      loadMoreObserver: null,
      // products
      products: [],
      productsLoading: false,
      productsApiCallId: null,
      productFilters: {
        storeEnabled: "enabled",
        page: 1,
        limitPerPage: "50",
        orderCol: "nkr_code",
        orderDir: "asc",
        search: "",
        type: "",
        dependencia: ""
      },
      totalItems: 0,
      totalPages: 0,
      productsByPage: "50",
      selectedProduct: null,
      showNewProductModal: false,
      // stats
      statsData: null,
      statsLoading: false
    };
  },
  computed: {
    ...mapGetters("general", [
      "getActiveStore",
      "getPriceLevels",
      "getActiveDepartment"
    ]),
    isSearchTab() {
      return this.activeTab === "search";
    },
    // El vehículo elegido: un solo chip con todas sus versiones (fitment).
    selectedVehicle() {
      return this.facetsSelection.find(s => s.type === "vehicle") || null;
    },
    // El vehículo por el que se filtra, venga de lo escrito (selectedVehicle)
    // o del selector de la barra lateral (fitment, un id): con él se verifica
    // cada resultado.
    vehicleFilter() {
      if (this.selectedVehicle) {
        return {
          ids: this.selectedVehicle.values.map(String),
          label: this.selectedVehicle.valueLabel
        };
      }
      const fitment = this.facetsSelection.find(s => s.type === "fitment");
      if (!fitment) return null;
      return {
        ids: [String(fitment.value)],
        label: fitment.valueLabel || `vehicle ${fitment.value}`
      };
    },
    // Los chips de filtros de siempre; el vehículo tiene su propia cabecera.
    chipFacets() {
      return this.facetsSelection.filter(s => s.type !== "vehicle");
    },
    // Un VIN en lo escrito (17 caracteres): { vin, index } o null.
    typedVin() {
      if (!this.isSearchTab) return null;
      const words = this.searchInput.split(/\s+/).filter(Boolean);
      const index = words.findIndex(isVin);
      return index === -1 ? null : { vin: words[index].toUpperCase(), index };
    },
    // Los últimos 8 de un VIN en lo escrito ("27003041", lo que suelen dar los
    // clientes): { tail, index } o null. Un VIN entero manda.
    typedVinTail() {
      if (!this.isSearchTab || this.typedVin) return null;
      const words = this.searchInput.split(/\s+/).filter(Boolean);
      const index = words.findIndex(isVinTail);
      return index === -1 ? null : { tail: words[index].toUpperCase(), index };
    },
    // Esos 8 buscados en nuestras órdenes: { status, vins } (findVinsByTail).
    vinTailLookup() {
      if (!this.typedVinTail) return null;
      return (
        this.vinTailStates[this.typedVinTail.tail] || {
          status: "loading",
          vins: []
        }
      );
    },
    // El VIN escrito, o el completo de una orden para sus últimos 8: su
    // decodificación (NHTSA) y nuestros vehículos que le corresponden
    // (vehicleFromVin).
    vinInfo() {
      if (this.selectedVehicle) return null;
      let vin;
      let index;
      let order = null;
      if (this.typedVin) {
        ({ vin, index } = this.typedVin);
      } else {
        const lookup = this.vinTailLookup;
        if (!lookup || !lookup.vins.length) return null;
        index = this.typedVinTail.index;
        ({ vin, lastOrder: order } = lookup.vins[0]);
      }
      const state = this.vinStates[vin] || { status: "loading" };
      const decoded = state.decoded || null;
      return {
        vin,
        order,
        status: state.status,
        decoded,
        label: decoded ? decodedLabel(decoded) : "",
        match:
          state.status === "done"
            ? vehicleFromVin(decoded, this.vehicleIndex, index)
            : null
      };
    },
    // Aviso cuando el VIN todavía no da un vehículo nuestro.
    vinNotice() {
      // Sin la búsqueda en órdenes (key o índice mal) no parece que "no hace
      // nada": se dice, salvo que el año ya haya dado el vehículo.
      const lookup = this.vinTailLookup;
      if (lookup && lookup.status === "error" && !this.detectedVehicle) {
        return `Could not look up VIN …${this.typedVinTail.tail} in orders`;
      }
      const info = this.vinInfo;
      if (!info || info.match) return "";
      if (info.status === "loading") {
        return info.order
          ? `Decoding VIN ${info.vin} (from order ${info.order})…`
          : `Decoding VIN ${info.vin}…`;
      }
      if (info.status === "error") {
        return `Could not reach the VIN decoder for ${info.vin}`;
      }
      if (!info.decoded.ok) return `VIN ${info.vin} could not be decoded`;
      return `VIN ${info.vin}: ${info.label} — not in our vehicle list`;
    },
    detectedVehicle() {
      if (!this.isSearchTab || this.selectedVehicle) return null;
      // Con un VIN manda el VIN: no se mezcla con lo que diga el texto.
      if (this.vinInfo) {
        const { match, vin, order, label } = this.vinInfo;
        return match
          ? {
              ...match,
              source: "vin",
              kicker: order ? `VIN from order ${order}` : "VIN decoded",
              detail: `VIN ${vin} · NHTSA: ${label}`
            }
          : null;
      }
      // Últimos 8 sin VIN completo en las órdenes: su año vale como año
      // escrito ("npr 27003041" = NPR 2002). Si el ciclo deja dos años con
      // vehículos ("S" = 1995 o 2025), no se elige ninguno.
      const lookup = this.vinTailLookup;
      if (lookup && lookup.status !== "loading") {
        const { tail, index } = this.typedVinTail;
        const words = this.searchInput.split(/\s+/).filter(Boolean);
        const byYear = vinYears(tail)
          .map(year => {
            const withYear = [...words];
            withYear[index] = String(year);
            return detectVehicle(withYear.join(" "), this.vehicleIndex);
          })
          .filter(vehicle => vehicle && vehicle.indices.includes(index));
        if (byYear.length === 1) {
          return {
            ...byYear[0],
            source: "vin",
            kicker: `Vehicle detected · VIN …${tail} = ${byYear[0].year}`
          };
        }
      }
      const vehicle = detectVehicle(this.searchInput, this.vehicleIndex);
      return vehicle
        ? { ...vehicle, source: "text", kicker: "Vehicle detected" }
        : null;
    },
    // El vehículo reconocido ya como UN vehículo concreto (marca, año, motor):
    // porque lo escrito basta ("isuzu npr-hd 2006 4hk1") o porque sólo hay
    // uno. Si no, null y se pide concretar (vehicleChoice).
    specificDetected() {
      const vehicle = this.detectedVehicle;
      if (!vehicle || vehicle.options.length !== 1) return null;
      return specificVehicle(vehicle, vehicle.options[0]);
    },
    detectedModelKey() {
      const vehicle = this.detectedVehicle;
      return vehicle ? `${vehicle.makes.join("/")}|${vehicle.model}` : null;
    },
    // Qué pedir para concretar: años (si no hay año) o motores de ese año.
    vehicleChoice() {
      const vehicle = this.detectedVehicle;
      if (!vehicle || this.specificDetected) return { title: "", options: [] };
      const year = vehicle.year || this.vehiclePickYear;
      if (!year) {
        return {
          title: "Choose the year",
          options: vehicle.years.map(y => ({
            key: `year-${y}`,
            label: String(y),
            year: y
          }))
        };
      }
      return {
        title: `Choose the engine · ${year}`,
        // Con varias marcas (Isuzu y General Motors NPR-HD), la marca delante.
        options: vehicle.options
          .filter(option => option.year === year)
          .map(option => ({
            key: option.key,
            label: [
              vehicle.makes.length > 1 ? option.make : null,
              vehicleEngineLabel(option)
            ]
              .filter(Boolean)
              .join(" · "),
            hint: option.family ? option.family.family : "",
            group: option
          }))
      };
    },
    detectedFilters() {
      if (!this.isSearchTab) return [];
      // Las palabras del vehículo ("4hk1" en "npr-hd 4hk1") no se proponen
      // otra vez como motor.
      return detectIntent(
        this.searchInput,
        this.intentVocabulary,
        this.facetsSelection,
        this.detectedVehicle ? this.detectedVehicle.indices : []
      );
    },
    // Qué muestra el panel bajo la barra: últimas búsquedas con la barra
    // vacía, sugerencias al escribir, o nada.
    panelMode() {
      if (!this.isSearchTab || !this.suggestFocused || this.panelDismissed) {
        return null;
      }
      if (!this.searchInput.trim()) {
        return this.recents.length ? "recent" : null;
      }
      return this.suggestions.length ||
        this.panelVehicleRows.length ||
        this.vinNotice
        ? "suggest"
        : null;
    },
    // Filas del vehículo reconocido arriba del autocompletado: la versión
    // concreta, o las opciones para llegar a ella (años más recientes o
    // motores del año).
    panelVehicleRows() {
      const vehicle = this.detectedVehicle;
      if (!vehicle) return [];
      if (this.specificDetected) {
        return [
          {
            key: "specific",
            kind: "specific",
            label: this.specificDetected.label,
            hint: this.specificDetected.detail,
            image: this.specificDetected.image
          }
        ];
      }
      const year = vehicle.year || this.vehiclePickYear;
      return this.vehicleChoice.options
        .slice(0, year ? undefined : PANEL_MAX_YEARS)
        .map(option => ({
          key: option.key,
          kind: option.year ? "year" : "version",
          label: option.year
            ? `${vehicle.base} ${option.year}`
            : `${option.group.make} ${
                vehicle.model
              } ${year} · ${vehicleEngineLabel(option.group)}`,
          hint: option.hint || "",
          image:
            option.group && option.group.family && option.group.family.imageUrl
              ? option.group.family.imageUrl
              : vehicle.image,
          option
        }));
    },
    vehiclePanelTitle() {
      const vehicle = this.detectedVehicle;
      if (!vehicle) return "";
      const source = vehicle.kicker;
      if (this.specificDetected) return source;
      return vehicle.year || this.vehiclePickYear
        ? `${source} · choose the engine`
        : `${source} · choose the year (or type it)`;
    },
    suggestOpen() {
      return this.panelMode !== null;
    },
    panelCount() {
      if (this.panelMode === "recent") return this.recents.length;
      if (this.panelMode === "suggest") {
        return this.panelVehicleRows.length + this.suggestions.length;
      }
      return 0;
    },
    recentsUserKey() {
      const user = this.$store.getters["auth/currentUser"] || {};
      return user.id || user.email || "anon";
    },
    // Lo escrito con las correcciones de Typesense ("nozle" -> "nozzle").
    correctedQuery() {
      const fixes = new Map(
        this.queryInfo.corrections.map(c => [c.from, c.to])
      );
      return this.searchInput
        .split(/\s+/)
        .filter(Boolean)
        .map(word => fixes.get(cleanWord(word)) || word)
        .join(" ");
    },
    // Palabras ignoradas que se pueden quitar tal cual (no las que eran un
    // trozo de otra con guion, "npr-hd").
    removableDroppedWords() {
      const dropped = new Set(this.queryInfo.dropped);
      return this.searchInput
        .split(/\s+/)
        .filter(word => dropped.has(cleanWord(word)));
    },
    // Filtros rápidos de disponibilidad con la cuenta de la búsqueda actual.
    // Sólo los que tienen resultados o están puestos.
    quickFilters() {
      if (!this.isSearchTab || !this.facets) return [];
      const dept = this.activeDeptCode;
      const options = [
        dept && {
          key: "dept-stock",
          type: `price_and_stock_by_store.${dept}.available`,
          value: "In Stock",
          label: `In stock · ${dept}`,
          icon: "mdi mdi-store-check-outline",
          tone: "tone-green"
        },
        {
          key: "any-stock",
          type: "price_and_stock.available",
          value: "In Stock",
          label: "In stock · any store",
          icon: "mdi mdi-package-variant-closed-check",
          tone: "tone-green"
        },
        {
          key: "incoming",
          type: "price_and_stock.available",
          value: "Incoming",
          label: "Incoming",
          icon: "mdi mdi-truck-delivery-outline",
          tone: "tone-blue"
        },
        {
          key: "backorder",
          type: "price_and_stock.available",
          value: "backorder",
          label: "Backorder",
          icon: "mdi mdi-clock-outline",
          tone: "tone-amber"
        }
      ].filter(Boolean);
      return options
        .map(option => ({
          ...option,
          count: (this.facets[option.type] || {})[option.value] || 0,
          on: this.facetsSelection.some(
            f => f.type === option.type && f.value === option.value
          )
        }))
        .filter(option => option.count > 0 || option.on);
    },
    activeDeptCode() {
      return this.getActiveDepartment?.code || null;
    },
    currentDepartmentPriceLevels() {
      const departamentoId = this.getActiveDepartment?.full?.departamentoId;
      if (!departamentoId) return [];
      return this.getPriceLevels.filter(
        level =>
          level.departamentoId === departamentoId &&
          this.canSeePriceLevel(level.localPriceLevel)
      );
    },
    sortOptions() {
      return SORT_OPTIONS;
    },
    priceLevels() {
      return this.currentDepartmentPriceLevels.map(level => ({
        label: level.codigo
          ? `${level.codigo} (${level.localPriceLevel})`
          : level.localPriceLevel,
        value: level.localPriceLevel
      }));
    },
    defaultPriceLevel() {
      const defaultLevel = this.currentDepartmentPriceLevels.find(
        level => level.default === "1" || level.default === 1
      );
      return defaultLevel?.localPriceLevel || "online";
    },
    productStatusOptions() {
      return [
        {
          displayText: this.$t(
            "message.products_page.statuses.enabled_to_store"
          ),
          value: "enabled"
        },
        {
          displayText: this.$t(
            "message.products_page.statuses.disabled_to_store"
          ),
          value: "disabled"
        },
        {
          displayText: this.$t(
            "message.products_page.statuses.disabled_to_store_with_stock"
          ),
          value: "disabled_with_stock"
        },
        {
          displayText: this.$t("message.products_page.statuses.all_status"),
          value: null
        }
      ];
    },
    tableHeaders() {
      return [
        {
          displayText: this.$t("message.products_page.table_headers.sku"),
          value: "nkr_code",
          visible: "w-36"
        },
        {
          displayText: "Brands stores",
          value: "store_brands",
          visible: "hidden sm:table-cell w-32"
        },
        {
          displayText: this.$t("message.products_page.table_headers.unificado"),
          value: "unificado",
          visible: "hidden sm:table-cell w-28"
        },
        {
          displayText: this.$t("message.products_page.table_headers.oe"),
          value: "oe",
          visible: "w-28"
        },
        {
          displayText: this.$t("message.products_page.table_headers.cost"),
          value: "cost",
          visible: this.userCanSeeCost ? "hidden sm:table-cell w-16" : "hidden"
        },
        {
          displayText: this.$t(
            "message.products_page.table_headers.custom_title"
          ),
          value: "original_title",
          visible: "hidden sm:table-cell min-w-['500px']"
        },
        {
          displayText: this.$t("message.products_page.table_headers.stock"),
          value: "stock",
          visible: "hidden sm:table-cell w-16"
        },
        {
          displayText: this.$t("message.products_page.table_headers.enabled"),
          value: "enabled",
          visible: "hidden sm:table-cell w-20"
        },
        {
          displayText: this.$t("message.products_page.table_headers.price"),
          value: "price",
          visible: "hidden sm:table-cell w-28"
        },
        {
          displayText: this.$t("message.products_page.table_headers.action"),
          value: "action",
          visible: "sm:hidden"
        }
      ];
    },
    dependenciaOptions() {
      return [
        { value: "A", label: "Solo stock" },
        { value: "B", label: "Stock + dropship" },
        { value: "B2", label: "Catálogo/dropship (vendible)" },
        { value: "E", label: "Solo stock" },
        { value: "M", label: "Solo stock" },
        { value: "V", label: "Stock + dropship" },
        { value: "X", label: "Solo stock" },
        { value: "Z", label: "Catálogo display (no vendible)" }
      ];
    },
    // precio4, 5 y 6 piden permiso; sin él la API no los devuelve.
    canSeePriceLevel() {
      return level =>
        !/^precio\d$/.test(level) ||
        this.$store.getters["auth/userHasPermission"](`price_${level}`);
    },
    userCanSeeCost() {
      return this.$store.getters["auth/userHasPermission"]("view_cost");
    },
    arrowDirection() {
      return this.productFilters.orderDir === "desc" ? "down" : "up";
    },
    currentYear() {
      return new Date().getFullYear();
    },
    returnRate() {
      if (!this.statsData) return "0.00";
      const sold = this.statsData.summary.overallUnitsSold;
      const returned = this.statsData.summary.overallUnitsReturned;
      if (!sold) return "0.00";
      return ((returned / sold) * 100).toFixed(2);
    },
    netRevenue() {
      if (!this.statsData) return 0;
      return (
        this.statsData.summary.overallRevenue -
        this.statsData.summary.overallReturnAmount
      );
    }
  },
  watch: {
    currentDepartmentPriceLevels(levels) {
      if (this.priceLevelFromUrl) return;
      if (levels.length > 0) {
        this.selectedPriceLevel = this.defaultPriceLevel;
      }
    },
    facetsSelection: {
      deep: true,
      handler() {
        if (this.isInitializing) return;
        this.findProducts();
      }
    },
    searchInput: {
      handler: debounce(function(val) {
        if (this.isInitializing) return;
        this.productFilters.search = val;
        this.productFilters.page = 1;
        this.syncQueryString();
        if (this.isSearchTab) {
          this.findProducts();
        } else if (this.activeTab === "products") {
          this.loadProducts();
        } else {
          this.loadStats();
        }
      }, 300)
    },
    sortBy() {
      if (this.isInitializing) return;
      this.syncQueryString();
      this.findProducts();
    },
    // Un VIN nuevo en lo escrito: se decodifica.
    typedVin(typed) {
      if (typed) this.decodeVinOnce(typed.vin);
    },
    // Unos últimos 8 nuevos: se buscan una vez en las órdenes y, si alguna
    // tiene el VIN completo, se decodifica. Si la tienda no tiene órdenes en
    // Typesense (o su índice aún no tiene infix en vin), queda sólo el año.
    typedVinTail(typed) {
      if (!typed || this.vinTailStates[typed.tail]) return;
      const { tail } = typed;
      this.$set(this.vinTailStates, tail, { status: "loading", vins: [] });
      findVinsByTail(tail)
        .then(vins => {
          this.$set(this.vinTailStates, tail, { status: "done", vins });
          if (vins.length) this.decodeVinOnce(vins[0].vin);
        })
        .catch(() =>
          this.$set(this.vinTailStates, tail, { status: "error", vins: [] })
        );
    },
    // Otro modelo en lo escrito: el año elegido en la tarjeta ya no vale.
    detectedModelKey() {
      this.vehiclePickYear = null;
    },
    explainRanking(on) {
      try {
        window.localStorage.setItem(EXPLAIN_STORAGE_KEY, on ? "1" : "0");
      } catch {
        // Sin almacenamiento sólo no se recuerda.
      }
    },
    activeTab(newTab) {
      if (newTab === "search") this.loadIntentVocabulary();
      this.syncQueryString();
      if (this.isSearchTab) {
        this.findProducts();
      } else if (newTab === "products") {
        this.loadProducts();
      } else if (newTab === "stats") {
        this.loadStats();
      }
    }
  },
  mounted() {
    if (this.$route.query?.q) {
      this.searchInput = this.$route.query.q;
      this.productFilters.search = this.$route.query.q;
    }
    if (this.$route.query?.priceLevel) {
      this.priceLevelFromUrl = true;
      this.selectedPriceLevel = this.$route.query.priceLevel;
    } else if (this.currentDepartmentPriceLevels.length > 0) {
      this.selectedPriceLevel = this.defaultPriceLevel;
    }
    if (this.$route.query?.filters) {
      try {
        this.facetsSelection = JSON.parse(this.$route.query.filters);
      } catch (e) {
        console.error("Failed to parse filters from URL", e);
      }
    }
    const view = this.$route.query?.view;
    if (view && !LEGACY_SEARCH_VIEWS.includes(view)) {
      this.activeTab = view;
    }
    if (this.activeTab === "search") this.loadIntentVocabulary();
    document.addEventListener("keydown", this.onGlobalKeydown);
    if (isSortOption(this.$route.query?.sort)) {
      this.sortBy = this.$route.query.sort;
    }

    this.$nextTick(() => {
      this.searchCallId = 0;
      this.findProducts().then(() => {
        this.isInitializing = false;
      });
    });

    if (this.$route.query?.productOpen) {
      this.openProduct(this.$route.query.productOpen);
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onGlobalKeydown);
    if (this.loadMoreObserver) this.loadMoreObserver.disconnect();
    if (this.showBarcodeReader) {
      this.showBarcodeReader = false;
    }
  },
  methods: {
    currentDeptStock(product) {
      const rows = product.pricesAndStock || [];
      const match = rows.find(
        r => r.departamentoCodigo === this.activeDeptCode
      );
      return match ? Number(match.stock) || 0 : 0;
    },
    allDeptStock(product) {
      const rows = product.pricesAndStock || [];
      let total = 0;
      let count = 0;
      for (const r of rows) {
        const s = Number(r.stock) || 0;
        total += s;
        if (s > 0) count++;
      }
      return { total, count };
    },
    syncQueryString() {
      const query = { ...this.$route.query };
      if (this.searchInput) {
        query.q = this.searchInput;
      } else {
        delete query.q;
      }
      if (this.activeTab && this.activeTab !== "search") {
        query.view = this.activeTab;
      } else {
        delete query.view;
      }
      if (this.sortBy !== DEFAULT_SORT) {
        query.sort = this.sortBy;
      } else {
        delete query.sort;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    setSearch(val) {
      this.searchInput = val;
      this.productFilters.search = val;
      this.productFilters.page = 1;
      if (this.isSearchTab) {
        this.findProducts();
      } else if (this.activeTab === "products") {
        this.loadProducts();
      } else if (this.activeTab === "stats") {
        this.loadStats();
      }
    },
    // Decodifica un VIN una vez (la caché de decodeVin dura toda la sesión).
    decodeVinOnce(vin) {
      if (this.vinStates[vin]) return;
      this.$set(this.vinStates, vin, { status: "loading" });
      decodeVin(vin)
        .then(decoded =>
          this.$set(this.vinStates, vin, { status: "done", decoded })
        )
        .catch(() => this.$set(this.vinStates, vin, { status: "error" }));
    },
    loadVehicleIndex() {
      const storeCode = this.getActiveStore?.code;
      if (!storeCode || this.vehicleIndexStore === storeCode) return;
      loadVehicles(storeCode)
        .then(vehicles => {
          this.vehicleIndex = buildVehicleIndex(vehicles);
          this.vehicleIndexStore = storeCode;
        })
        // Sin la colección de vehículos (search-sync todavía no la creó) sólo
        // no se reconocen vehículos; la búsqueda sigue.
        .catch(e => console.warn("Search vehicles:", e));
    },
    loadIntentVocabulary() {
      this.loadVehicleIndex();
      const collection = this.getActiveStore?.code;
      if (!collection) return;
      if (this.intentCollection === collection && this.intentVocabulary) {
        return;
      }
      loadFacetValues({ collection, fields: INTENT_FIELDS })
        .then(values => {
          this.intentVocabulary = buildVocabulary(values);
          this.intentCollection = collection;
        })
        // Sin vocabulario sólo no se proponen filtros; la búsqueda sigue.
        .catch(e => console.warn("Search facet values:", e));
    },
    // Resultados de la búsqueda actual con ese valor (facets de Typesense).
    intentCount(match) {
      return (this.facets[match.type] || {})[match.value] || null;
    },
    // Aplica filtros reconocidos y, si viene, el vehículo: quita sus palabras
    // del texto (todas a la vez, para que las posiciones sigan valiendo) y
    // los añade a la selección.
    applyIntent(matches, vehicle = null) {
      this.closeSuggestions();
      const indices = matches.flatMap(match =>
        match.words.map((_, index) => match.start + index)
      );
      if (vehicle) indices.push(...vehicle.indices);
      const selection = vehicle
        ? // Un solo vehículo: sustituye al anterior y al del selector.
          this.facetsSelection.filter(
            s => s.type !== "vehicle" && s.type !== "fitment"
          )
        : this.facetsSelection;
      // Texto y filtros en el mismo tick: el watcher de facetsSelection busca
      // ya con el texto nuevo, y el del texto repite la misma búsqueda, que
      // sale de la caché.
      this.searchInput = removeWordIndices(this.searchInput, indices);
      this.facetsSelection = [
        ...selection,
        ...matches.map(match => ({
          type: match.type,
          value: match.value,
          valueLabel: `${match.kind}: ${match.display}`
        })),
        ...(vehicle
          ? [
              {
                type: "vehicle",
                field: "fitment",
                value: vehicle.label,
                values: vehicle.ids,
                valueLabel: vehicle.label,
                detail: vehicle.detail,
                image: vehicle.image
              }
            ]
          : [])
      ];
    },
    // Un año deja el panel abierto con los motores de ese año; una versión
    // la elige y sigue la búsqueda con el resto del texto.
    pickVehicleRow(row) {
      if (row.kind === "year") {
        this.vehiclePickYear = row.option.year;
        this.suggestIndex = -1;
        return;
      }
      if (row.kind === "specific") {
        this.applyIntent([], this.specificDetected);
        return;
      }
      this.pickVehicleOption(row.option);
    },
    pickVehicleOption(option) {
      if (option.year) {
        this.vehiclePickYear = option.year;
        return;
      }
      this.applyIntent([], specificVehicle(this.detectedVehicle, option.group));
    },
    removeVehicle() {
      this.facetsSelection = this.facetsSelection.filter(
        s => s.type !== "vehicle"
      );
    },
    // Sólo lo que escribe el usuario pide sugerencias: al elegir una, el
    // v-model cambia sin evento input y la lista no se vuelve a abrir.
    onSearchTyped() {
      if (!this.isSearchTab) return;
      this.panelDismissed = false;
      this.suggestIndex = -1;
      this.suggestFor = this.searchInput;
      this.fetchSuggestions();
    },
    fetchSuggestions: debounce(async function() {
      if (this.suggestFor === null || this.suggestFor !== this.searchInput) {
        return;
      }
      const callId = ++this.suggestCallId;
      try {
        const res = await suggestProducts({
          collection: this.getActiveStore.code,
          query: this.searchInput,
          limit: SUGGEST_LIMIT,
          verifyFitment: this.vehicleFilter ? this.vehicleFilter.ids : null
        });
        // Una respuesta que llega tarde no pisa la de lo último escrito.
        if (callId !== this.suggestCallId) return;
        this.suggestions = res.suggestions;
        this.suggestFound = res.found;
        this.suggestTimeMs = res.searchTimeMs;
      } catch (e) {
        if (callId === this.suggestCallId) this.suggestions = [];
      }
    }, SUGGEST_DEBOUNCE_MS),
    moveSuggestion(step) {
      if (!this.suggestOpen) return;
      const count = this.panelCount;
      this.suggestIndex = (this.suggestIndex + step + count + 1) % (count + 1);
      if (this.suggestIndex === count) this.suggestIndex = -1;
    },
    // Enter: la fila marcada (sugerencia o búsqueda reciente) o, sin nada
    // marcado, lo escrito tal cual. En los dos casos queda en recientes.
    pickSuggestion(index = this.suggestIndex) {
      // Las primeras filas del panel son las del vehículo reconocido.
      const vehicleRows =
        this.panelMode === "suggest" ? this.panelVehicleRows : [];
      if (index >= 0 && index < vehicleRows.length) {
        this.pickVehicleRow(vehicleRows[index]);
        return;
      }
      const value =
        this.panelMode === "recent"
          ? this.recents[index]
          : (this.suggestions[index - vehicleRows.length] || {}).value;
      this.closeSuggestions();
      if (value) this.searchInput = value;
      this.rememberSearch(value || this.searchInput);
    },
    openSuggestion(suggestion) {
      this.rememberSearch(this.searchInput);
      this.closeSuggestions();
      this.openProduct(suggestion.code);
    },
    closeSuggestions() {
      this.suggestCallId++;
      this.suggestFor = null;
      this.suggestions = [];
      this.suggestIndex = -1;
      this.panelDismissed = true;
    },
    onSearchFocus() {
      this.suggestFocused = true;
      this.panelDismissed = false;
      this.loadRecents();
    },
    onSearchBlur() {
      this.suggestFocused = false;
      this.suggestIndex = -1;
    },
    // Esc: cierra el panel; con el panel ya cerrado, suelta la barra.
    onSearchEscape() {
      if (this.suggestOpen) this.closeSuggestions();
      else this.$refs.searchInput.blur();
    },
    // ⌘/Ctrl+Enter: abre el primer resultado.
    openFirstResult() {
      if (!this.isSearchTab || !this.items.length) return;
      this.closeSuggestions();
      this.openResult(this.items[0].nkrCode[0]);
    },
    // Clic en una tarjeta: además, la búsqueda queda en recientes.
    openResult(code) {
      this.rememberSearch(this.searchInput);
      this.openProduct(code);
    },
    // "/" pone el foco en la barra desde cualquier parte de la página.
    onGlobalKeydown(event) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey) return;
      if (!this.isSearchTab || this.showProductModal) return;
      const target = event.target;
      const tag = (target.tagName || "").toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tag) ||
        target.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      this.$refs.searchInput.focus();
      this.$refs.searchInput.select();
    },
    // ── Últimas búsquedas (utils/searchRecents.js) ──
    loadRecents() {
      this.recents = readRecents(
        this.recentsUserKey,
        this.getActiveStore?.code
      );
    },
    rememberSearch(query) {
      if (!this.isSearchTab) return;
      this.recents = addRecent(
        this.recentsUserKey,
        this.getActiveStore?.code,
        query
      );
    },
    removeRecentSearch(query) {
      this.recents = removeRecent(
        this.recentsUserKey,
        this.getActiveStore?.code,
        query
      );
      this.suggestIndex = -1;
    },
    clearRecentSearches() {
      this.recents = clearRecents(
        this.recentsUserKey,
        this.getActiveStore?.code
      );
      this.suggestIndex = -1;
    },
    // ── "Quisiste decir" ──
    useCorrectedQuery() {
      this.searchInput = this.correctedQuery;
      this.rememberSearch(this.searchInput);
    },
    removeDroppedWords() {
      const drop = new Set(this.removableDroppedWords);
      this.searchInput = this.searchInput
        .split(/\s+/)
        .filter(word => word && !drop.has(word))
        .join(" ");
    },
    // ── Filtros rápidos ──
    toggleQuickFilter(quick) {
      if (quick.on) {
        this.facetsSelection = this.facetsSelection.filter(
          f => !(f.type === quick.type && f.value === quick.value)
        );
      } else {
        this.facetsSelection = [
          ...this.facetsSelection,
          { type: quick.type, value: quick.value, valueLabel: quick.label }
        ];
      }
    },
    // ── Modo "por qué salió" ──
    rankingReason(ranking) {
      if (ranking.sortBy !== "relevance") {
        const option = this.sortOptions.find(o => o.value === ranking.sortBy);
        return option ? `sorted by ${this.$t(option.labelKey)}` : "sorted";
      }
      if (!ranking.textSearch) {
        return `no text: by popularity (${ranking.popularity})`;
      }
      return ranking.tied
        ? `same text score as its neighbours → popularity ${ranking.popularity}`
        : "best text match first";
    },
    // ── Scroll infinito ──
    observeLoadMore() {
      if (this.loadMoreObserver) this.loadMoreObserver.disconnect();
      if (!this.$refs.loadMoreSentinel || !window.IntersectionObserver) return;
      this.loadMoreObserver = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.loadMoreTypesense();
          }
        },
        // Pide la página siguiente antes de llegar al final.
        { rootMargin: "800px 0px" }
      );
      this.loadMoreObserver.observe(this.$refs.loadMoreSentinel);
    },
    async loadMoreTypesense() {
      if (!this.isSearchTab || !this.tsHasMore || this.tsLoadingMore) return;
      const apiId = this.searchCallId;
      const page = this.tsPage + 1;
      this.tsLoadingMore = true;
      try {
        const res = await searchTypesense({
          ...this.typesenseRequest(),
          page
        });
        // Otra búsqueda empezó mientras tanto: esta página ya no vale.
        if (apiId !== this.searchCallId) return;
        const seen = new Set(this.items.map(item => item.objectID));
        this.items = [
          ...this.items,
          ...res.hits.filter(hit => !seen.has(hit.objectID))
        ];
        this.tsPage = page;
        this.tsHasMore = res.hits.length > 0 && this.items.length < res.found;
      } catch (e) {
        console.error("Typesense load more:", e);
        this.tsHasMore = false;
      } finally {
        this.tsLoadingMore = false;
        this.$nextTick(this.observeLoadMore);
      }
    },
    availabilityClass(available) {
      const value = String(available).toLowerCase();
      if (value === "in stock") return "in-stock";
      if (value === "out of stock") return "out-of-stock";
      return "other";
    },
    clearSearch() {
      this.closeSuggestions();
      this.searchInput = "";
      this.productFilters.search = "";
      this.productFilters.page = 1;
      this.items = [];
      if (this.isSearchTab) {
        this.findProducts();
      } else if (this.activeTab === "products") {
        this.loadProducts();
      } else if (this.activeTab === "stats") {
        this.loadStats();
      }
    },
    openProduct(code) {
      this.openedProduct = code;
      this.showProductModal = true;
    },
    // ── Búsqueda ──
    deleteFacet(facet) {
      this.facetsSelection = this.facetsSelection.filter(
        s => !(s.type === facet.type && s.value === facet.value)
      );
      this.findProducts();
    },
    clearAllFacets() {
      this.facetsSelection = [];
      this.findProducts();
    },
    getItemPrice(item) {
      if (!this.selectedPriceLevel) return null;
      const byStore = item.price_and_stock_by_store;
      const currentStore = byStore && byStore[this.activeDeptCode];
      // El sync solo llena el precio de una tienda si tiene stock ahi; sin stock
      // se cae al precio global (el mismo por el que ordenan las replicas).
      const storePrice = currentStore && currentStore[this.selectedPriceLevel];
      if (storePrice) return storePrice;
      if (!item.price_and_stock) return null;
      return item.price_and_stock[this.selectedPriceLevel] || null;
    },
    getItemLocation(item) {
      if (!item.locations || !this.activeDeptCode) return null;
      const loc = item.locations[this.activeDeptCode];
      if (!loc) return null;
      return Array.isArray(loc) ? loc.join(", ") : loc;
    },
    barcodeOnDecode(code) {
      this.setSearch(code);
      this.closeReader();
    },
    barcodeOnLoaded(e) {
      console.log(e);
    },
    closeReader() {
      this.showBarcodeReader = false;
      if (this.$refs.scanner) {
        this.$refs.scanner.codeReader.stream
          .getTracks()
          .forEach(track => track.stop());
      }
    },
    // La búsqueda actual para searchTypesense (página 1; loadMoreTypesense
    // pide las siguientes con los mismos parámetros).
    typesenseRequest() {
      return {
        collection: this.getActiveStore.code,
        query: this.searchInput,
        facetsSelection: this.facetsSelection,
        sortBy: this.sortBy,
        hitsPerPage: TYPESENSE_PAGE_SIZE,
        verifyFitment: this.vehicleFilter ? this.vehicleFilter.ids : null
      };
    },
    // Devuelve hits y facets con la forma que pinta la página (ver
    // utils/productSearch.js). El tiempo se mide de ida y vuelta desde el
    // navegador.
    async findProducts() {
      this.searchCallId++;
      const apiId = this.searchCallId;
      this.searchLoading = true;
      this.searchError = null;
      try {
        const startedAt = performance.now();
        const res = await searchTypesense(this.typesenseRequest());
        if (apiId < this.searchCallId) return;
        this.searchStats = {
          found: res.found,
          ms: Math.round(performance.now() - startedAt)
        };
        this.items = res.hits;
        this.facets = res.facets;
        this.queryInfo = res.queryInfo || EMPTY_QUERY_INFO;
        this.tsPage = 1;
        this.tsHasMore = res.hits.length < res.found;
        this.$nextTick(this.observeLoadMore);
      } catch (e) {
        console.error("Typesense error:", e);
        this.searchError = e.message;
      } finally {
        if (apiId >= this.searchCallId) {
          this.searchLoading = false;
        }
      }
    },
    // ── Products ──
    changeStatusFilter(value) {
      this.productFilters.storeEnabled = value;
      this.productFilters.page = 1;
      this.loadProducts();
    },
    onDependenciaChange() {
      this.productFilters.page = 1;
      this.loadProducts();
    },
    async loadProducts() {
      this.productsApiCallId = new Date();
      const apiId = this.productsApiCallId;
      this.productsLoading = true;
      const res = await getProducts(this.productFilters).catch(() => {
        console.log("Error getting Products");
      });
      if (apiId !== this.productsApiCallId) return;
      this.products = res.products;
      this.totalItems = res.totalItems;
      this.totalPages = res.totalPages;
      this.productsLoading = false;
    },
    previous() {
      if (this.productFilters.page > 1) {
        this.productFilters.page--;
        this.loadProducts();
      }
    },
    next() {
      if (this.productFilters.page < this.totalPages) {
        this.productFilters.page++;
        this.loadProducts();
      }
    },
    onPageSizeChange() {
      this.productFilters.limitPerPage = this.productsByPage;
      this.productFilters.page = 1;
      this.loadProducts();
    },
    toQuantity() {
      return (
        this.productFilters.limitPerPage * (this.productFilters.page - 1) +
        this.products.length
      );
    },
    changeArrowDir(headerValue) {
      this.productFilters.page = 1;
      if (headerValue === this.productFilters.orderCol) {
        this.productFilters.orderDir =
          this.productFilters.orderDir === "desc" ? "asc" : "desc";
      } else {
        this.productFilters.orderCol = headerValue;
        this.productFilters.orderDir = "desc";
      }
      this.loadProducts();
    },
    updateProductStatus(code, enabled) {
      changeProductStatus(code, enabled).catch(e => {
        console.error("Error updating product status:", e);
      });
    },
    checkProductUpdate(product) {
      const p = this.products.find(p => p.nkrCode === product.nkrCode);
      if (p) p.enabled = product.enabled;
    },
    onProductDeleted(code) {
      this.openedProduct = "";
      // Quitar de la lista local sin recargar todo el listado.
      this.products = this.products.filter(p => p.nkrCode !== code);
    },
    async loadStats() {
      if (!this.searchInput.trim()) {
        this.statsData = null;
        return;
      }
      this.statsLoading = true;
      try {
        this.statsData = await getProductStats(this.searchInput.trim());
      } catch (e) {
        console.error("Error loading product stats:", e);
      } finally {
        this.statsLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.products-search-page {
  .search-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .price-level-select {
      height: 48px;
      padding: 0 12px;
      font-size: 14px;
      color: #4b5563;
      border: 2px solid #d1d5db;
      border-radius: 6px;
      background: white;
      flex-shrink: 0;
    }

    .search-wrap {
      position: relative;
      flex-grow: 1;

      .clear-btn {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        padding: 0 8px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        z-index: 1;
        line-height: 26px;
      }

      .search-input {
        width: 100%;
        height: 48px;
        padding: 0 40px 0 14px;
        font-size: 20px;
        color: #4b5563;
        border: 2px solid #d1d5db;
        border-radius: 6px;
        outline: none;

        &:focus {
          border-color: #374151;
        }
      }
    }

    .suggest-panel {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      z-index: 60;
      background: white;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      box-shadow: 0 12px 28px rgba(17, 24, 39, 0.16);
      overflow: hidden;
    }

    .suggest-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      cursor: pointer;
      border-bottom: 1px solid #f3f4f6;

      // El ratón sólo pinta el fondo; la fila elegida con ↑↓ no cambia.
      // .is-active y no .active: _base-modal.vue fuerza .active a
      // display: block en toda la app y la fila perdía su layout.
      &:hover,
      &.is-active {
        background: #eff6ff;
      }

      ::v-deep em {
        background: #fef08a;
        font-style: normal;
        font-weight: 700;
      }
    }

    .suggest-thumb {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f9fafb;
      border-radius: 6px;
      overflow: hidden;
      color: #d1d5db;
      font-size: 20px;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .suggest-body {
      flex: 1;
      min-width: 0;
    }

    .suggest-head {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    .suggest-code {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
    }

    .suggest-brand {
      font-size: 12px;
      font-weight: 600;
      color: #2563eb;
    }

    .suggest-available {
      margin-left: auto;
      flex-shrink: 0;
      font-size: 11px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 9999px;
      text-transform: capitalize;

      &.in-stock {
        background: #dcfce7;
        color: #166534;
      }

      &.out-of-stock {
        background: #f3f4f6;
        color: #6b7280;
      }

      &.other {
        background: #fef3c7;
        color: #92400e;
      }
    }

    .suggest-match {
      display: flex;
      align-items: baseline;
      gap: 6px;
      font-size: 13px;
      color: #374151;
    }

    .suggest-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      color: #6b7280;
      background: #f3f4f6;
      border-radius: 4px;
      padding: 1px 5px;
    }

    .suggest-title {
      font-size: 12px;
      color: #6b7280;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .suggest-open {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      border-radius: 6px;
      color: #6b7280;
      font-size: 18px;

      &:hover {
        background: #dbeafe;
        color: #1d4ed8;
      }
    }

    .suggest-footer {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      padding: 6px 10px;
      font-size: 11px;
      color: #6b7280;
      background: #f9fafb;
    }

    .barcode-btn {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    // Mobile: búsqueda + lector en la primera fila, selects en la segunda
    @media screen and (max-width: 639px) {
      flex-wrap: wrap;

      .search-wrap {
        order: -2;
        flex: 1 1 60%;
        min-width: 0;
      }

      .barcode-btn {
        order: -1;
      }

      .price-level-select {
        flex: 1 1 40%;
        min-width: 0;
      }
    }
  }

  .barcode-box {
    max-width: 500px;
    margin: 16px auto;
  }

  .tabs-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;

    .tab-bar {
      display: flex;
      flex-direction: row;
      gap: 4px;
    }

    .search-stats {
      font-size: 12px;
      opacity: 0.7;
      margin-right: auto;
    }

    .search-error {
      font-size: 12px;
      color: red;
    }

    .tab-btn {
      padding: 8px 20px;
      border-radius: 6px;
      border: 1px solid #d1d5db;
      background: white;
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;

      &.active {
        background: #1d4ed8;
        color: white;
        border-color: #1d4ed8;
      }

      &:not(.active):hover {
        background: #f3f4f6;
      }
    }
  }

  // ── Search layout ──
  .search-layout {
    display: flex;
    flex-direction: row;
    gap: 16px;
    position: relative;
  }

  .filters {
    display: none;
    flex-shrink: 0;

    &.mobile-show {
      display: block;
      width: 100%;
      padding-bottom: 70px;
    }
  }

  .search-items-area {
    flex-grow: 1;
    position: relative;

    &.mobile-show {
      display: none;
    }
  }

  .show-filters-btn {
    position: fixed;
    bottom: 16px;
    left: calc(50% - 70px);
    width: fit-content;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: #fbbf24;
    border-radius: 6px;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    white-space: nowrap;
  }

  .tab-content {
    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 120px;
      gap: 8px;
      color: #6b7280;
    }

    .items {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;

      &.loading {
        opacity: 0.2;
      }
    }

    .item {
      background: white;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        border-color: #d1d5db;
        transform: translateY(-2px);
      }
    }

    .item-image {
      width: 100%;
      height: 140px;
      background: #f9fafb;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px;
      border-bottom: 1px solid #f3f4f6;
      flex-shrink: 0;

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
    }

    .item-info {
      flex-grow: 1;
      padding: 12px;
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .item-header {
      display: flex;
      flex-direction: column;
      margin-bottom: 4px;
    }

    .item-code {
      font-size: 16px;
      font-weight: 700;
      color: #111827;
      line-height: 1.3;
    }

    .item-brand {
      font-size: 14px;
      font-weight: 500;
      color: #2563eb;
    }

    .item-unificado {
      align-self: flex-start;
      font-size: 12px;
      color: #4b5563;
      background: #f3f4f6;
      padding: 2px 8px;
      border-radius: 4px;
      border: 1px solid #d1d5db;
      margin: 4px 0;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: #dbeafe;
        color: #1d4ed8;
      }
    }

    .item-title {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.4;
      margin-bottom: 8px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;

      ::v-deep em {
        background: #fef08a;
        font-style: normal;
        font-weight: 500;
      }
    }

    .item-price {
      font-size: 20px;
      font-weight: 700;
      color: #16a34a;
      margin-top: auto;
    }

    .item-matches {
      display: flex;
      flex-direction: column;
      gap: 3px;
      margin-bottom: 8px;
      font-size: 12px;
    }

    .item-match {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 4px;
      min-width: 0;
    }

    .match-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: #6b7280;
      background: #f3f4f6;
      border-radius: 4px;
      padding: 1px 5px;
    }

    .match-value {
      color: #374151;
      overflow-wrap: anywhere;

      & + .match-value::before {
        content: "· ";
        color: #9ca3af;
      }

      ::v-deep em {
        background: #fef08a;
        font-style: normal;
        font-weight: 600;
      }
    }

    .match-more {
      color: #9ca3af;
    }

    .item-location {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      font-size: 12px;

      .loc-label {
        font-weight: 600;
        color: #374151;
        background: #f3f4f6;
        padding: 1px 6px;
        border-radius: 3px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }

      .loc-value {
        font-weight: 600;
        color: #b45309;
        letter-spacing: 0.02em;
      }
    }

    .item-stock {
      flex-shrink: 0;
      background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
      padding: 10px 12px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 8px;
      border-top: 1px solid #e5e7eb;

      .stock-total {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 6px;
      }

      .stock-label {
        font-size: 12px;
        color: #9ca3af;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .stock-value {
        font-size: 16px;
        font-weight: 700;
        color: #16a34a;
      }
    }

    .stock-breakdown {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .store-stock {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
      font-size: 12px;

      .store-name {
        font-weight: 600;
        color: #374151;
        background: white;
        padding: 1px 6px;
        border-radius: 3px;
      }

      .warehouse-stock {
        color: #6b7280;

        strong {
          color: #2563eb;
        }
      }
    }

    // ── Status filter bar (Products tab) ──
    .status-filter-bar {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      margin-bottom: 12px;

      .status-btn {
        padding: 5px 14px;
        border-radius: 20px;
        border: 1px solid #d1d5db;
        background: white;
        color: #6b7280;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;

        &.active {
          background: #1d4ed8;
          color: white;
          border-color: #1d4ed8;
        }

        &:not(.active):hover {
          background: #f3f4f6;
        }
      }

      .dep-select {
        height: 32px;
        padding: 0 8px;
        font-size: 13px;
        color: #4b5563;
        border: 1px solid #d1d5db;
        border-radius: 20px;
        background: white;
        cursor: pointer;
      }
    }

    .products-table-wrap {
      overflow-x: auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
    }

    .products-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14px;
      background: white;

      thead tr {
        background: #f3f4f6;
        border-bottom: 2px solid #e5e7eb;
      }

      th {
        padding: 10px 12px;
        text-align: left;
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        white-space: nowrap;
        cursor: pointer;
        position: relative;
        border-right: 1px solid #e5e7eb;

        &:last-child {
          border-right: none;
        }

        .sort-icon {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          img {
            width: 14px;
          }
        }
      }

      tbody tr {
        border-bottom: 1px solid #e5e7eb;
        cursor: pointer;
        transition: background 0.1s;

        &:last-child {
          border-bottom: none;
        }
        &:nth-child(even) {
          background: #fafafa;
        }
        &:hover {
          background: #eef4ff;
        }
        &.selected {
          background: #dbeafe;
        }
      }

      td {
        padding: 8px 12px;
        vertical-align: middle;
        border-right: 1px solid #e5e7eb;
        color: #111827;

        &:last-child {
          border-right: none;
        }
      }

      .sku-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .sku-code {
          font-weight: 700;
          color: #1d4ed8;
          font-size: 14px;

          &.match {
            background: #fca5a5;
            padding: 1px 4px;
            border-radius: 4px;
            color: #111827;
          }
        }

        .sku-brand {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }
      }

      .store-brands-cell {
        font-size: 12px;
        color: #6b7280;
        vertical-align: middle;
      }

      .code-btn {
        font-size: 13px;
        color: #374151;
        text-align: left;
        padding: 2px 4px;
        border-radius: 4px;
        transition: background 0.1s;

        &:hover {
          background: #dbeafe;
          color: #1d4ed8;
        }
        &.match {
          background: #fca5a5;
          color: #111827;
        }
      }

      .title-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .title-custom {
          font-size: 13px;
          color: #111827;
          font-weight: 500;
        }

        .title-original {
          font-size: 12px;
          color: #6b7280;
        }
      }

      .stock-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 13px;
        align-items: center;

        .stock-label {
          font-size: 11px;
          color: #6b7280;
          margin-right: 4px;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .stock-value {
          font-weight: 600;
          color: #111827;
        }

        .stock-dpts {
          font-size: 11px;
          color: #6b7280;
          font-weight: 400;
        }
      }

      .prices-cell {
        position: relative;
        display: flex;
        justify-content: center;

        .prices-btn {
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          color: #fff;
          background: #4b5563;
          border-radius: 4px;
          border: none;
          cursor: default;
        }

        .prices-popover {
          display: none;
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 6px;
          z-index: 50;
          background: #fff;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
          padding: 6px;
          min-width: 360px;
        }

        &:hover .prices-popover {
          display: block;
        }

        .prices-popover-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;

          th,
          td {
            padding: 4px 6px;
            border-bottom: 1px solid #f3f4f6;
            text-align: right;
            white-space: nowrap;
          }

          th:first-child,
          td:first-child {
            text-align: left;
            font-weight: 600;
          }

          thead th {
            color: #6b7280;
            font-weight: 600;
            background: #f9fafb;
          }

          tr.is-current {
            background: #fef3c7;
          }
        }

        @media screen and (min-width: 1920px) {
          .prices-btn {
            display: none;
          }

          .prices-popover {
            display: block;
            position: static;
            margin-top: 0;
            box-shadow: none;
            border: none;
            padding: 0;
            min-width: 0;
          }
        }
      }
    }
  }
}

/* Tablet - 2 columns for items */
@media screen and (min-width: 768px) {
  .products-search-page .tab-content .items {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Desktop - filters sidebar appears, 3 columns */
@media screen and (min-width: 1000px) {
  .products-search-page {
    .filters {
      display: block;
      width: 255px;
    }

    .show-filters-btn {
      display: none;
    }

    .tab-content .items {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

/* Large Desktop */
@media screen and (min-width: 1400px) {
  .products-search-page .tab-content .items {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Extra Large Desktop */
@media screen and (min-width: 1700px) {
  .products-search-page .tab-content .items {
    grid-template-columns: repeat(5, 1fr);
  }
}

/* ── Stats Tab ── */
.products-search-page .tab-content {
  .stats-header {
    font-size: 15px;
    color: #6b7280;
    margin-bottom: 16px;
    display: flex;
    align-items: center;

    strong {
      color: #111827;
    }
  }

  .stats-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 24px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  .stat-card {
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid transparent;

    .stat-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.7;
    }

    .stat-value {
      font-size: 22px;
      font-weight: 700;
      line-height: 1.1;
    }

    .stat-sub {
      font-size: 11px;
      opacity: 0.65;
      margin-top: 2px;
    }

    &.green {
      background: #f0fdf4;
      border-color: #bbf7d0;
      color: #14532d;
    }
    &.blue {
      background: #eff6ff;
      border-color: #bfdbfe;
      color: #1e3a8a;
    }
    &.gray {
      background: #f9fafb;
      border-color: #e5e7eb;
      color: #374151;
    }
    &.red {
      background: #fff1f2;
      border-color: #fecdd3;
      color: #881337;
    }
    &.orange {
      background: #fff7ed;
      border-color: #fed7aa;
      color: #7c2d12;
    }
    &.teal {
      background: #f0fdfa;
      border-color: #99f6e4;
      color: #134e4a;
    }
  }

  .stats-table-wrap {
    overflow-x: auto;

    .stats-table {
      min-width: 640px;

      th {
        text-align: center;
        font-size: 12px;
        padding: 8px 12px;
        white-space: nowrap;
      }

      td {
        padding: 8px 12px;
        font-size: 13px;
        white-space: nowrap;
      }

      tr.current-year {
        background: #eff6ff;

        td {
          font-weight: 600;
        }
      }
    }
  }

  .stats-empty {
    color: #9ca3af;
    font-size: 14px;
    padding: 40px 0;
    text-align: center;
  }
}
// Filtros reconocidos en lo escrito (Typesense)
.intent-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  background: linear-gradient(90deg, #eef2ff 0%, #f5f3ff 100%);

  .intent-title {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #4338ca;
    margin-right: 4px;
  }

  .intent-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px 3px 6px;
    font-size: 13px;
    color: #1f2937;
    background: white;
    border: 1px solid #a5b4fc;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: #e0e7ff;
      border-color: #6366f1;
    }

    .mdi-plus {
      color: #6366f1;
    }
  }

  .intent-kind {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: #6366f1;
  }

  .intent-value {
    font-weight: 600;
  }

  .intent-count {
    font-size: 11px;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 9999px;
    padding: 0 6px;
  }

  .intent-apply-all {
    margin-left: auto;
    padding: 3px 12px;
    font-size: 12px;
    font-weight: 600;
    color: white;
    background: #4f46e5;
    border-radius: 9999px;

    &:hover {
      background: #4338ca;
    }
  }
}
// Búsquedas recientes en el panel de la barra (Typesense)
.suggest-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  background: #f9fafb;

  .suggest-clear {
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    color: #dc2626;

    &:hover {
      text-decoration: underline;
    }
  }
}

.recent-row {
  .recent-icon {
    color: #9ca3af;
    font-size: 18px;
  }

  .recent-text {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// "Explain ranking" junto a las estadísticas
.explain-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4338ca;
  cursor: pointer;
  user-select: none;

  input {
    accent-color: #4f46e5;
  }
}

// "Quisiste decir" / palabras ignoradas
.query-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #1e3a8a;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;

  &.is-warning {
    color: #78350f;
    background: #fffbeb;
    border-color: #fde68a;
  }

  .query-note-muted {
    color: #6b7280;
  }

  .query-note-action {
    margin-left: auto;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    color: inherit;
    border: 1px solid currentColor;
    border-radius: 9999px;

    &:hover {
      background: white;
    }
  }
}

// Filtros rápidos de disponibilidad
.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.quick-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  .quick-count {
    font-size: 11px;
    font-weight: 600;
    padding: 0 6px;
    border-radius: 9999px;
    background: #f3f4f6;
    color: #6b7280;
  }

  &.tone-green {
    &:hover,
    &.is-on {
      border-color: #16a34a;
      color: #166534;
    }
    &.is-on {
      background: #dcfce7;
    }
  }

  &.tone-blue {
    &:hover,
    &.is-on {
      border-color: #2563eb;
      color: #1e40af;
    }
    &.is-on {
      background: #dbeafe;
    }
  }

  &.tone-amber {
    &:hover,
    &.is-on {
      border-color: #d97706;
      color: #92400e;
    }
    &.is-on {
      background: #fef3c7;
    }
  }
}

// Modo "por qué salió"
.item {
  position: relative;
}

.rank-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  min-width: 28px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  color: white;
  background: #4f46e5;
  border-radius: 9999px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.rank-explain {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  margin-bottom: 8px;
  font-size: 11px;
  line-height: 1.4;
  color: #374151;
  background: #f5f3ff;
  border: 1px dashed #c4b5fd;
  border-radius: 6px;
  cursor: default;

  .rank-key {
    display: inline-block;
    min-width: 66px;
    font-weight: 700;
    color: #6d28d9;
  }
}

// Scroll infinito
.load-more {
  display: flex;
  justify-content: center;
  padding: 16px 0 24px;

  .load-more-btn {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 9999px;

    &:hover {
      background: #f3f4f6;
    }
  }
}
// Vehículo reconocido / elegido (Typesense)
.vehicle-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 10px;

  &.is-detected {
    background: linear-gradient(90deg, #ecfeff 0%, #eff6ff 100%);
    border: 1px solid #a5f3fc;
  }

  &.is-selected {
    background: #f0fdf4;
    border: 1px solid #86efac;
  }

  .vehicle-image {
    flex-shrink: 0;
    width: 88px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    color: #0e7490;
    font-size: 34px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .vehicle-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .vehicle-kicker {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #0e7490;
  }

  &.is-selected .vehicle-kicker {
    color: #15803d;
  }

  .vehicle-name {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
  }

  .vehicle-detail {
    font-size: 12px;
    color: #4b5563;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .vehicle-select {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    color: white;
    background: #0e7490;
    border-radius: 9999px;

    &:hover {
      background: #155e75;
    }
  }

  .vehicle-remove {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 9999px;
    color: #6b7280;
    font-size: 18px;

    &:hover {
      background: #dcfce7;
      color: #166534;
    }
  }

  @media screen and (max-width: 639px) {
    flex-wrap: wrap;

    .vehicle-select {
      width: 100%;
      justify-content: center;
    }
  }
}
.vehicle-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;

  .vehicle-options-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #0e7490;
    margin-right: 2px;
  }

  .vehicle-option {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    background: white;
    border: 1px solid #67e8f9;
    border-radius: 9999px;

    &:hover {
      background: #cffafe;
      border-color: #0e7490;
    }

    &.is-back {
      color: #6b7280;
      border-color: #d1d5db;
    }

    .vehicle-option-hint {
      font-size: 11px;
      font-weight: 400;
      color: #6b7280;
    }
  }
}
.suggest-section.is-vehicle {
  color: #0e7490;
  background: #ecfeff;

  .is-back {
    color: #0e7490;
  }
}

.vehicle-row {
  .suggest-thumb {
    width: 56px;
    color: #0e7490;
    font-size: 24px;
  }

  .vehicle-row-action {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: #0e7490;
    padding: 3px 10px;
    border: 1px solid #67e8f9;
    border-radius: 9999px;
    background: white;
  }
}
// Doble verificación del vehículo en cada tarjeta
.fit-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  border-radius: 6px;

  i {
    font-size: 15px;
  }

  &.is-fit {
    color: #166534;
    background: #dcfce7;
    border: 1px solid #86efac;
  }

  &.is-unknown {
    color: #92400e;
    background: #fef3c7;
    border: 1px solid #fcd34d;
  }
}
// Autocompletado con vehículo elegido
.suggest-fit {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 9999px;

  &.is-fit {
    color: #166534;
    background: #dcfce7;
  }

  &.is-no {
    color: #6b7280;
    background: #f3f4f6;
  }
}

.suggest-row.is-nofit {
  .suggest-thumb,
  .suggest-code,
  .suggest-match,
  .suggest-title {
    opacity: 0.55;
  }
}
</style>
